import React, { useCallback, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { apiErrorMessage, apiFetch } from '@/lib/csrf'
import PricingReviewDialog, { type ReviewHeading } from '@/components/review/PricingReviewDialog'
import type { PricingReview, ReviewConflictsPayload, ReviewError } from '@/types/review'

/**
 * REVIEW BEFORE SAVE, FOR ANY PRICING PAGE (docs/plans/2026-10-20-change-safety.md §5.17.1).
 *
 *   const review = usePricingReview()
 *   review.open(`${property.basePath}/pricing-rules/day-of-week/review`, payload, () =>
 *     inertiaSave('patch', `${property.basePath}/pricing-rules/day-of-week`, payload))
 *   return <>{form}{review.dialog}</>
 *
 * `open` posts the draft (the exact body the save sends) to the review endpoint through `apiFetch`,
 * so the CSRF token is read at request time, never from the frozen meta tag. The server rehearses
 * the save's own mutation and rolls it back (R1.5, R1.8); the dialog shows what it answers. Nothing
 * is saved until the person confirms, and then `onConfirm` runs: the page's own save call,
 * unchanged.
 *
 * THE CONFIRM. `onConfirm` receives the review (an undo needs to know whether it had conflicts).
 *   - It returns nothing: the dialog closes at once and the page's save reports as it does today.
 *   - It returns a promise: the dialog stays open with the confirm disabled until it settles. It
 *     closes when the promise resolves. When it rejects, the review stays on screen with the reason
 *     in a rose panel, and the person can try again. Reject with `SaveRefusedError` to show the
 *     server's sentences, and with `{ refreshReview: true }` to fetch the review again under them
 *     (an undo that lost a race: "Something changed since you reviewed this undo.").
 *   `inertiaSave` is the promise for an Inertia save: it settles when the visit finishes, so the
 *   dialog shows the save running, and the page's own callbacks still report the outcome.
 *
 * A REFUSED REVIEW (any non-200 the server explains) keeps its sentences in the dialog, under the
 * hotel and business the 422 names, and hands them to `onRefused` with their `roomTypeId`s, so a
 * page that anchors errors to rows (Rooms Setup) can show them there too.
 *
 * A response that arrives after the dialog closed, or after a newer `open`, is dropped, and its
 * request is aborted.
 */

/** The spec's network sentence for a review that could not be fetched (§5.17.1). */
export const REVIEW_NETWORK_SENTENCE = "We couldn't check your changes. They are still here, so try again."

/**
 * The same sentence for a confirmed save that failed without a server reason. Client chrome, in
 * the voice of the spec's review sentence; a save with a reason rejects with `SaveRefusedError`.
 */
export const SAVE_NETWORK_SENTENCE = "We couldn't save your changes. They are still here, so try again."

/** A review, or an undo review carrying its conflicts (§5.11.2). */
export type ReviewResponse = PricingReview & Partial<ReviewConflictsPayload>

export type ReviewConfirm = (review: ReviewResponse) => void | Promise<unknown>

export interface OpenReviewOptions {
  /** Shows "Back" instead of "Cancel": the review closes and this reopens the form, draft intact. */
  onBack?: () => void
  /** The server refused the review: its sentences, each with the room it names (Rooms Setup). */
  onRefused?: (errors: ReviewError[]) => void
}

export interface PricingReviewController {
  open: (endpoint: string, draft: object, onConfirm: ReviewConfirm, options?: OpenReviewOptions) => void
  close: () => void
  isOpen: boolean
  /** Render this once, anywhere in the page. */
  dialog: React.ReactNode
}

/** A save the server refused, with its sentences. */
export class SaveRefusedError extends Error {
  readonly messages: string[]
  readonly refreshReview: boolean

  constructor(messages: string[], options: { refreshReview?: boolean } = {}) {
    super(messages.join(' '))
    this.name = 'SaveRefusedError'
    this.messages = messages
    this.refreshReview = options.refreshReview ?? false
  }
}

type SaveMethod = 'post' | 'patch' | 'put' | 'delete'
// The router's own option and payload types, read off its signature: @inertiajs/core is only a
// transitive dependency, so it is never imported by name.
type VisitOptions = NonNullable<Parameters<typeof router.patch>[2]>
type VisitData = Parameters<typeof router.patch>[1]

/**
 * An Inertia save as a promise for `onConfirm`: the same `router[method](url, data, options)` call
 * the page always made, settling when the visit finishes, whatever its outcome. The page's own
 * `onError`, `onSuccess` and `onFinish` run as before, so a refused save reports exactly where it
 * always did (a flash, or Rooms Setup's anchored panel) and the dialog closes onto it.
 */
export function inertiaSave(method: SaveMethod, url: string, data: object = {}, options: VisitOptions = {}): Promise<void> {
  return new Promise((resolve) => {
    const visit: VisitOptions = {
      ...options,
      onFinish: (finished) => {
        options.onFinish?.(finished)
        resolve()
      },
    }
    if (method === 'delete') {
      // Inertia's delete takes its body in the options, never as a second argument.
      router.delete(url, Object.keys(data).length > 0 ? { ...visit, data: data as VisitData } : visit)
    } else {
      router[method](url, data as VisitData, visit)
    }
  })
}

interface ReviewState {
  open: boolean
  loading: boolean
  review: ReviewResponse | null
  errors: string[] | null
  heading: ReviewHeading | null
  saving: boolean
  hasBack: boolean
}

interface ReviewRequest {
  id: number
  endpoint: string
  body: string
  onConfirm: ReviewConfirm
  onBack?: () => void
  onRefused?: (errors: ReviewError[]) => void
  controller: AbortController | null
}

const CLOSED: ReviewState = {
  open: false,
  loading: false,
  review: null,
  errors: null,
  heading: null,
  saving: false,
  hasBack: false,
}

export function usePricingReview(): PricingReviewController {
  const [state, setState] = useState<ReviewState>(CLOSED)
  // The live request. `seq` outlives it, so a close or a newer open makes every pending answer stale.
  const request = useRef<ReviewRequest | null>(null)
  const seq = useRef(0)
  // A save is one save: a second click that lands before React re-renders the disabled confirm
  // must not run the page's save again. State alone cannot promise that; a ref can.
  const saving = useRef(false)
  const isCurrent = (id: number) => request.current?.id === id && seq.current === id

  const load = useCallback(async (id: number, keepErrors: boolean) => {
    const r = request.current
    if (!r || r.id !== id) return

    r.controller?.abort()
    const controller = new AbortController()
    r.controller = controller
    setState((s) => ({ ...s, loading: true, review: null, errors: keepErrors ? s.errors : null }))

    try {
      const response = await apiFetch(r.endpoint, { method: 'POST', body: r.body, signal: controller.signal })
      const body = await readJson(response)
      if (!isCurrent(id)) return

      if (response.ok && isReview(body)) {
        setState((s) => ({ ...s, loading: false, review: body }))
        return
      }

      const refusal = readRefusal(body)
      setState((s) => ({
        ...s,
        loading: false,
        review: null,
        heading: refusal.heading,
        errors: refusal.items.length > 0 ? refusal.items.map((e) => e.message) : [REVIEW_NETWORK_SENTENCE],
      }))
      if (refusal.items.length > 0) r.onRefused?.(refusal.items)
    } catch (e) {
      if (!isCurrent(id)) return
      setState((s) => ({ ...s, loading: false, review: null, errors: [apiErrorMessage(e, REVIEW_NETWORK_SENTENCE)] }))
    }
    // isCurrent reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const open = useCallback<PricingReviewController['open']>(
    (endpoint, draft, onConfirm, options = {}) => {
      request.current?.controller?.abort()
      const id = ++seq.current
      request.current = {
        id,
        endpoint,
        body: JSON.stringify(draft),
        onConfirm,
        onBack: options.onBack,
        onRefused: options.onRefused,
        controller: null,
      }
      saving.current = false
      setState({ ...CLOSED, open: true, loading: true, hasBack: !!options.onBack })
      void load(id, false)
    },
    [load]
  )

  // The review keeps its content while the dialog fades out; the next `open` resets it.
  const close = useCallback(() => {
    seq.current += 1
    request.current?.controller?.abort()
    request.current = null
    saving.current = false
    setState((s) => ({ ...s, open: false, saving: false }))
  }, [])

  useEffect(
    () => () => {
      seq.current += 1
      request.current?.controller?.abort()
    },
    []
  )

  const back = () => {
    const onBack = request.current?.onBack
    close()
    onBack?.()
  }

  const confirm = async () => {
    const r = request.current
    const review = state.review
    if (!r || !review || review.empty || saving.current || state.loading) return
    const id = r.id
    saving.current = true

    setState((s) => ({ ...s, saving: true, errors: null }))

    const fail = (e: unknown) => {
      if (!isCurrent(id)) return
      saving.current = false
      if (e instanceof SaveRefusedError) {
        setState((s) => ({ ...s, saving: false, errors: e.messages }))
        if (e.refreshReview) void load(id, true)
      } else {
        setState((s) => ({ ...s, saving: false, errors: [apiErrorMessage(e, SAVE_NETWORK_SENTENCE)] }))
      }
    }

    let result: void | Promise<unknown>
    try {
      result = r.onConfirm(review)
    } catch (e) {
      fail(e)
      return
    }

    if (!isThenable(result)) {
      if (isCurrent(id)) close()
      return
    }

    try {
      await result
      if (isCurrent(id)) close()
    } catch (e) {
      fail(e)
    }
  }

  const dialog = React.createElement(PricingReviewDialog, {
    open: state.open,
    review: state.review,
    errors: state.errors,
    heading: state.heading,
    saving: state.saving,
    loading: state.loading,
    onConfirm: () => void confirm(),
    onCancel: close,
    onBack: state.hasBack ? back : undefined,
    conflicts: state.review?.conflicts ?? null,
    confirmOverwriteLabel: state.review?.confirmOverwriteLabel ?? null,
  })

  return { open, close, isOpen: state.open, dialog }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json()
  } catch {
    // A signed-out session follows a redirect to an HTML page; that is a refusal, not a review.
    return null
  }
}

// Parse at the boundary (EP-21): a 200 that is not a review (an HTML page reached through a
// redirect) must never render as one.
function isReview(body: unknown): body is ReviewResponse {
  if (!body || typeof body !== 'object') return false
  const b = body as Record<string, unknown>
  return typeof b.title === 'string' && Array.isArray(b.rows) && typeof b.empty === 'boolean'
}

/**
 * The server's own sentences, whatever the status: every review answers `errors:
 * [{ message, roomTypeId }]` on a 422, 403, 404 and 409 (the 409 keeps a legacy `error` too), and a
 * 422 names the hotel and the business it refused. A bare body (a 500, an HTML page) carries none,
 * and the caller falls back to the spec's network sentence.
 */
function readRefusal(body: unknown): { items: ReviewError[]; heading: ReviewHeading | null } {
  if (!body || typeof body !== 'object') return { items: [], heading: null }
  const b = body as { errors?: unknown; error?: unknown; title?: unknown; businessName?: unknown }

  let items: ReviewError[] = []
  if (Array.isArray(b.errors)) {
    items = b.errors.flatMap((e): ReviewError[] => {
      if (typeof e === 'string') return e.trim() ? [{ message: e, roomTypeId: null }] : []
      const item = e as { message?: unknown; roomTypeId?: unknown } | null
      if (typeof item?.message !== 'string' || item.message.trim() === '') return []
      return [{ message: item.message, roomTypeId: typeof item.roomTypeId === 'number' ? item.roomTypeId : null }]
    })
  }
  if (items.length === 0 && typeof b.error === 'string' && b.error.trim() !== '') {
    items = [{ message: b.error, roomTypeId: null }]
  }

  const heading =
    typeof b.title === 'string' && typeof b.businessName === 'string'
      ? { title: b.title, businessName: b.businessName }
      : null

  return { items, heading }
}

function isThenable(value: unknown): value is Promise<unknown> {
  return !!value && typeof (value as { then?: unknown }).then === 'function'
}
